/* eslint-disable sonarjs/cognitive-complexity */

import { useSelector } from 'react-redux';

import * as lookups from 'modules/api/lookups';
import Input from 'lib/Input';
import { InputType } from 'components/common/types/Input.types';
import AsyncSelect from 'lib/AsyncSelect';
import closeSvg from 'assets/icons/close.svg';
import Button, { Color, Size } from 'lib/Button';

import { getIsReseller } from 'store/publisher/selectors';
import { FilterAdvertiser, FilterBrand, FilterDealMultiId } from 'components/common/FilterArea/Filters';
import FilterProductCategoryLookup from 'components/common/FilterArea/Filters/FilterProductCategoryLookup';
import { cancelFunctions } from 'utils/cancelFunctions';
import { CodeNameModel } from 'components/common/types';
import { AsyncSelectLoadDataHandler } from 'lib/AsyncSelect/AsyncSelect.types';
import usePendoTracking from 'customHooks/usePendoTracking';
import FilterUploadDate from './FilterUploadDate';
import AdvancedFiltersProps from './AdvancedFilters.types';

const AdvancedFilters: React.FC<AdvancedFiltersProps> = ({
  onChange,
  productFormatOptions,
  advancedFilterData: {
    advertiser,
    brand,
    productCategory,
    externalAdvertiserName,
    clickThroughUrl,
    externalAgency,
    productFormat,
    dealIds,
    width,
    height,
    startDate,
    endDate,
  },
  onClearAll,
  changeFilters,
}) => {
  const { pathname } = window.location;
  const { getPendoDataId } = usePendoTracking(pathname);
  const isReseller = useSelector(getIsReseller);
  const FILTER_STYLE = 'flex-grow flex-shrink-0 min-w-62.5 max-w-100';

  const onLoadLookupData: AsyncSelectLoadDataHandler<CodeNameModel> = async (dropdownName, query) => {
    try {
      return await lookups.getLookupData({ cancelFunctions, lookupURLName: dropdownName, query });
    } catch (error) {
      return [];
    }
  };

  return (
    <div className="contents">
      {!isReseller && (
        <>
          <div className={`${FILTER_STYLE} mr-4`}>
            <FilterAdvertiser changeFilters={changeFilters} selectedAdvertiser={advertiser} />
          </div>
          <div className={FILTER_STYLE}>
            <FilterBrand changeFilters={changeFilters} brand={brand} />
          </div>
          <div className={FILTER_STYLE}>
            <FilterProductCategoryLookup changeFilters={changeFilters} productCategory={productCategory} />
          </div>{' '}
        </>
      )}
      <div className={FILTER_STYLE}>
        <Input
          name="externalAdvertiserName"
          value={externalAdvertiserName}
          placeholder="DSP advertiser name"
          dataTestId="externalAdvertiserName-filter"
          dataPendoId={getPendoDataId('DSP advertiser name filter')}
          onChange={(value: string) => onChange(value, 'externalAdvertiserName')}
        />
      </div>
      <div className={FILTER_STYLE}>
        <Input
          name="clickThroughUrl"
          value={clickThroughUrl}
          placeholder="DSP advertiser domain"
          dataTestId="clickThroughUrl-filter"
          dataPendoId={getPendoDataId('DSP advertiser domain filter')}
          onChange={(value: string) => onChange(value, 'clickThroughUrl')}
        />
      </div>
      <div className={FILTER_STYLE}>
        <Input
          name="externalAgencyId"
          value={externalAgency}
          placeholder="DSP seat ID"
          dataTestId="externalAgencyId-filter"
          dataPendoId={getPendoDataId('DSP seat ID filter')}
          onChange={(value: string) => onChange(value, 'externalAgency')}
        />
      </div>
      {!isReseller && (
        <div className={FILTER_STYLE}>
          <AsyncSelect
            dataTestId="product-select"
            name="productFormat"
            loadData={onLoadLookupData}
            defaultOptions={productFormatOptions}
            selectedValue={productFormat?.code ? productFormat : null}
            placeholder="Format"
            onSelect={(name: string, value: CodeNameModel) => {
              onChange(value, name);
            }}
            dataPendoId={getPendoDataId('Format filter')}
          />
        </div>
      )}
      <div className={`${FILTER_STYLE} xl:max-w-88`}>
        <FilterDealMultiId changeFilters={changeFilters} dealIds={dealIds} />
      </div>

      <div className={FILTER_STYLE}>
        <Input
          name="width"
          value={width}
          onChange={(value: string) => onChange(value, 'width')}
          placeholder="Width"
          type={InputType.NUMBER}
          dataPendoId={getPendoDataId('Width filter')}
        />
      </div>
      <div className={FILTER_STYLE}>
        <Input
          name="height"
          value={height}
          onChange={(value: string) => onChange(value, 'height')}
          placeholder="Height"
          type={InputType.NUMBER}
          dataPendoId={getPendoDataId('Height filter')}
        />
      </div>

      <FilterUploadDate
        uploadFrom={startDate || undefined}
        uploadTo={endDate || undefined}
        onChange={({ startDate: newStartDate, endDate: newEndDate }) =>
          changeFilters({ startDate: newStartDate, endDate: newEndDate })
        }
      />

      <div className="flex-grow text-right">
        <Button onClick={onClearAll} size={Size.LARGE} color={Color.TERTIARY} svg={closeSvg} label="Clear all" />
      </div>
    </div>
  );
};

export default AdvancedFilters;

/* eslint-enable sonarjs/cognitive-complexity */
